import React, { useState, useEffect } from 'react';
import './Transforms.css';
import { DEVICES } from '../../constants/breakpoints';

const Transforms = ({
  selectedElement,
  updateElementStyles,
  selectedDevice
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [transforms, setTransforms] = useState({
    translate: { x: 0, y: 0, z: 0 },
    rotate: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1 },
    skew: { x: 0, y: 0 }
  });
  const [expandedTransforms, setExpandedTransforms] = useState({
    translate: false,
    rotate: false,
    scale: false,
    skew: false
  });

  useEffect(() => {
    if (selectedElement?.styles) {
      let transformString;

      // Get transform based on device cascade
      if (selectedDevice === DEVICES.MOBILE) {
        transformString = selectedElement.styles?.[DEVICES.MOBILE]?.transform ||
                         selectedElement.styles?.[DEVICES.TABLET]?.transform ||
                         selectedElement.styles?.[DEVICES.DESKTOP]?.transform ||
                         selectedElement.styles?.transform;
      } else if (selectedDevice === DEVICES.TABLET) {
        transformString = selectedElement.styles?.[DEVICES.TABLET]?.transform ||
                         selectedElement.styles?.[DEVICES.DESKTOP]?.transform ||
                         selectedElement.styles?.transform;
      } else {
        transformString = selectedElement.styles?.[DEVICES.DESKTOP]?.transform ||
                         selectedElement.styles?.transform;
      }

      if (transformString) {
        const newTransforms = {
          translate: {
            x: parseInt(transformString.match(/translateX\(([-\d.]+)px\)/)?.[1] || 0),
            y: parseInt(transformString.match(/translateY\(([-\d.]+)px\)/)?.[1] || 0),
            z: parseInt(transformString.match(/translateZ\(([-\d.]+)px\)/)?.[1] || 0)
          },
          rotate: {
            x: parseInt(transformString.match(/rotateX\(([-\d.]+)deg\)/)?.[1] || 0),
            y: parseInt(transformString.match(/rotateY\(([-\d.]+)deg\)/)?.[1] || 0),
            z: parseInt(transformString.match(/rotateZ\(([-\d.]+)deg\)/)?.[1] || 0)
          },
          scale: {
            x: parseFloat(transformString.match(/scaleX\(([-\d.]+)\)/)?.[1] || 1),
            y: parseFloat(transformString.match(/scaleY\(([-\d.]+)\)/)?.[1] || 1)
          },
          skew: {
            x: parseInt(transformString.match(/skewX\(([-\d.]+)deg\)/)?.[1] || 0),
            y: parseInt(transformString.match(/skewY\(([-\d.]+)deg\)/)?.[1] || 0)
          }
        };
        setTransforms(newTransforms);
      }
    }
  }, [selectedElement, selectedDevice]);

  const handleTransformChange = (type, axis, value) => {
    const newTransforms = {
      ...transforms,
      [type]: {
        ...transforms[type],
        [axis]: value
      }
    };
    setTransforms(newTransforms);
    updateTransforms(newTransforms);
  };

  const updateTransforms = (newTransforms) => {
    const transformString = `
      translateX(${newTransforms.translate.x}px)
      translateY(${newTransforms.translate.y}px)
      translateZ(${newTransforms.translate.z}px)
      rotateX(${newTransforms.rotate.x}deg)
      rotateY(${newTransforms.rotate.y}deg)
      rotateZ(${newTransforms.rotate.z}deg)
      scaleX(${newTransforms.scale.x})
      scaleY(${newTransforms.scale.y})
      skewX(${newTransforms.skew.x}deg)
      skewY(${newTransforms.skew.y}deg)
    `.replace(/\s+/g, ' ').trim();

    updateElementStyles(
      selectedElement.id,
      "transform",
      transformString,
      null,
      selectedDevice
    );
  };

  const toggleTransform = (transformName) => {
    setExpandedTransforms(prev => ({
      ...prev,
      [transformName]: !prev[transformName]
    }));
  };

  const getTransformPreview = (type) => {
    switch(type) {
      case 'translate':
        return `${transforms.translate.x}, ${transforms.translate.y}, ${transforms.translate.z}`;
      case 'rotate':
        return `${transforms.rotate.x}, ${transforms.rotate.y}, ${transforms.rotate.z}`;
      case 'scale':
        return `${transforms.scale.x}, ${transforms.scale.y}`;
      case 'skew':
        return `${transforms.skew.x}, ${transforms.skew.y}`;
      default:
        return '';
    }
  };

  return (
    <div className="transforms-section">
      <div className="section-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="section-title">
          <i className="fas fa-vector-square"></i>
          <span>Transform</span>
        </div>
        <div className="section-controls">
          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
        </div>
      </div>

      {isExpanded && (
        <div className="transforms-controls">
          {[
            {
              type: 'translate',
              label: 'Translate',
              axes: ['x', 'y', 'z'],
              unit: 'px',
              min: -500,
              max: 500
            },
            {
              type: 'rotate',
              label: 'Rotate',
              axes: ['x', 'y', 'z'],
              unit: 'deg',
              min: -360,
              max: 360
            },
            {
              type: 'scale',
              label: 'Scale',
              axes: ['x', 'y'],
              unit: '',
              min: 0,
              max: 2,
              step: 0.1
            },
            {
              type: 'skew',
              label: 'Skew',
              axes: ['x', 'y'],
              unit: 'deg',
              min: -90,
              max: 90
            }
          ].map(transform => (
            <div key={transform.type} className="transform-item">
              <div 
                className="transform-header"
                onClick={() => toggleTransform(transform.type)}
              >
                <span className="transform-title">{transform.label}</span>
                <div className="transform-preview">
                  <span className="transform-value">{getTransformPreview(transform.type)}</span>
                  <i className={`fas fa-chevron-${expandedTransforms[transform.type] ? 'down' : 'right'}`}></i>
                </div>
              </div>
              {expandedTransforms[transform.type] && (
                <div className="transform-content">
                  {transform.axes.map(axis => (
                    <div key={axis} className="transform-control">
                      <label>{axis.toUpperCase()}</label>
                      <div className="slider-input-group">
                        <input
                          type="range"
                          min={transform.min}
                          max={transform.max}
                          step={transform.step || 1}
                          value={transforms[transform.type][axis]}
                          onChange={(e) => handleTransformChange(
                            transform.type,
                            axis,
                            transform.step ? parseFloat(e.target.value) : parseInt(e.target.value)
                          )}
                        />
                        <input
                          type="number"
                          min={transform.min}
                          max={transform.max}
                          step={transform.step || 1}
                          value={transforms[transform.type][axis]}
                          onChange={(e) => handleTransformChange(
                            transform.type,
                            axis,
                            transform.step ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0
                          )}
                        />
                        <span className="unit">{transform.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Transforms; 